/**
 * Sound on/off toggle — small icon button at the top-right of the HUD. It
 * decides whether our SFX actually play by gating the SoundManager (external
 * clips only); the .riv's own in-canvas speaker button is managed separately
 * and is not touched here. The choice persists to localStorage.
 */
const STORAGE_KEY = 'fpc_sound_on';
const ICON_ON = '🔊';
const ICON_OFF = '🔇';

export interface SoundToggleOptions {
  /** Used when nothing is stored yet. Defaults to true (sound on). */
  defaultOn?: boolean;
  /** Called on mount with the resolved state, then on every user toggle. */
  onChange: (on: boolean) => void;
}

export class SoundToggle {
  private readonly button: HTMLButtonElement;
  private readonly onChange: (on: boolean) => void;
  private on: boolean;

  constructor(opts: SoundToggleOptions) {
    const host = document.getElementById('dom-hud');
    if (!host) throw new Error('SoundToggle: #dom-hud missing');

    this.onChange = opts.onChange;
    this.on = readStored(opts.defaultOn ?? true);

    this.button = document.createElement('button');
    this.button.className = 'sound-toggle';
    this.button.addEventListener('click', () => this.set(!this.on, true));
    host.appendChild(this.button);

    this.render();
    // Push the resolved (possibly persisted) state to the consumer.
    this.onChange(this.on);
  }

  get isOn(): boolean {
    return this.on;
  }

  private set(on: boolean, notify: boolean): void {
    if (on === this.on) return;
    this.on = on;
    try {
      localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch {
      /* storage unavailable (private mode) — keep in-memory state */
    }
    this.render();
    if (notify) this.onChange(on);
  }

  private render(): void {
    this.button.classList.toggle('off', !this.on);
    this.button.textContent = this.on ? ICON_ON : ICON_OFF;
    const label = this.on ? 'Sound on' : 'Sound off';
    this.button.title = label;
    this.button.setAttribute('aria-label', label);
    this.button.setAttribute('aria-pressed', String(this.on));
  }
}

function readStored(fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === '1') return true;
    if (v === '0') return false;
  } catch {
    /* storage unavailable — fall through */
  }
  return fallback;
}
